import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useRef } from 'react';
import { offset } from '@floating-ui/core';
import { useFloating, useFocus, useInteractions, useClick, useDismiss, flip, arrow as arrowMiddleware, FloatingArrow, autoUpdate, } from '@floating-ui/react';
import ReactDOM from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import { useTheme } from 'styled-components';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import Button from '../../Controls/Button';
import Icons from '../../Elements/Icons';
import { ANIMATION_DURATION_MS } from './constants';
import Item from './Item';
import { ButtonComponent, ButtonWrapper, Wrapper } from './styled';
import { TheadButton } from './TheadButton';
function selectPlacement(align) {
    switch (align) {
        case 'right':
            return 'bottom-start';
        case 'left':
            return 'bottom-end';
        default:
            return 'bottom-start';
    }
}
const DropdownList = ({ id, className, dataTestId, children, variant = 'edit', disabled = false, buttonComponent, inThead, align = 'left', zIndex = 3, arrow = true, minWidth = true, onVisibleChange, trackHiddenReference, maxHeight, }) => {
    const theme = useTheme();
    const [isShown, setIsShown] = React.useState(false);
    const isChildrenArrayEmpty = Array.isArray(children) ? children.every((c) => !!c === false) : false;
    const arrowRef = useRef(null);
    const { refs, floatingStyles, context, placement } = useFloating({
        open: isShown,
        placement: selectPlacement(align),
        whileElementsMounted: autoUpdate,
        onOpenChange: setIsShown,
        middleware: [
            offset(5),
            flip({
                fallbackPlacements: ['bottom-end', 'bottom-start', 'top-end', 'top-start'],
            }),
            // eslint-disable-next-line react-hooks/refs
            arrowMiddleware({
                element: arrowRef,
            }),
        ],
    });
    useEffect(() => {
        onVisibleChange?.(isShown);
    }, [isShown, onVisibleChange]);
    const focus = useFocus(context);
    const click = useClick(context);
    const { getReferenceProps, getFloatingProps } = useInteractions([click, focus]);
    useDismiss(context, {
        enabled: isShown,
    });
    const isTop = placement.startsWith('top');
    const stopPropagation = useCallbackRef((evt) => evt.stopPropagation());
    if (isChildrenArrayEmpty) {
        return null;
    }
    return (_jsxs(_Fragment, { children: [ReactDOM.createPortal(_jsx(CSSTransition, { in: isShown, transitionAppear: true, timeout: ANIMATION_DURATION_MS, unmountOnExit: true, children: _jsxs(Wrapper, { ref: refs.setFloating, ...getFloatingProps(), style: floatingStyles, "$visible": isShown, "$isTop": isTop, "$zIndex": zIndex, "$minWidth": minWidth, "$trackHiddenReference": trackHiddenReference, "$maxHeight": maxHeight, children: [arrow && _jsx(FloatingArrow, { ref: arrowRef, context: context, fill: theme.colors.grey00_1, width: 11, height: 5 }), children] }) }), document.body), _jsxs(ButtonWrapper, { onClick: stopPropagation, children: [inThead && (_jsx(TheadButton, { id: id, 
                        // eslint-disable-next-line react-hooks/refs
                        ref: refs.setReference, ...getReferenceProps(), className: className, "data-testid": dataTestId, variant: variant, disabled: disabled, square: true, active: isShown, children: _jsx(Icons.Angle, { size: 16, color: theme.colors.grey00_1 }) })), buttonComponent && (_jsx(ButtonComponent, { ref: refs.setReference, ...getReferenceProps(), children: buttonComponent })), !buttonComponent && !inThead && (_jsx(Button, { id: id, className: className, "data-testid": dataTestId, variant: variant, disabled: disabled, square: true, 
                        // eslint-disable-next-line react-hooks/refs
                        ref: refs.setReference, ...getReferenceProps(), children: _jsx(Icons.Angle, { size: 16, color: theme.colors.grey00_2 }) }))] })] }));
};
const DropdownListMemo = React.memo(DropdownList);
const DropdownListWrapper = (props) => _jsx(DropdownListMemo, { ...props });
DropdownListWrapper.Item = Item;
export default DropdownListWrapper;
